import { Guid } from "@/Common/Models/Guid";
import { FlowsheetClient } from "../Api/FlowsheetClient";
import getFlowsheetResponseToFlowsheet from "../Mappers/GetFlowsheetResponseToFlowsheetMapper";
import { Flowsheet } from "../Models/Flowsheet";
import useFlowsheetStore from "./useFlowsheetStore";
import { IUseQueryResponse, useQuery } from "./useQuery";

/**
 * Custom hook to retrieve and manage a flowsheet for a given patient.
 * @param patientId - The ContactFacetId of the patient.
 * @param encounterId - The encounter id of the flowsheet.
 * @param flowsheetId - The ID of the flowsheet to retrieve.
 * @returns An object containing the flowsheet data and query status.
 */
function useFlowsheet(patientId: Guid, flowsheetId: Guid, templateId: Guid): IUseQueryResponse<Flowsheet> {
    const { getFlowsheet, saveFlowsheet, shouldReloadFlowsheet } = useFlowsheetStore(patientId);

    return useQuery({
        query: async () => {
            const storedFlowsheet = getFlowsheet(flowsheetId);
            const forceServerFetch = shouldReloadFlowsheet(flowsheetId) || storedFlowsheet?.TemplateId !== templateId;

            if (storedFlowsheet && !forceServerFetch) {
                return storedFlowsheet;
            }

            const response = await FlowsheetClient.getFlowsheet(flowsheetId);

            response.throwIfError();

            const flowsheet = getFlowsheetResponseToFlowsheet(response.data);

            if (storedFlowsheet) {
                flowsheet.applyPendingModificationsToComponents(storedFlowsheet.CareComponents);
            }

            saveFlowsheet(flowsheet);

            return flowsheet;
        },
        queryKey: ["flowsheet", flowsheetId, "templateId", templateId]
    });
}

export {
    useFlowsheet
};
